/**
 * WorkOrderManual - Interactive user manual for the Work Orders module.
 * Located in System Preferences > Work Orders > Manual tab.
 */
import React, { useState } from "react";
import {
  Box, Typography, Accordion, AccordionSummary, AccordionDetails,
  Chip, Paper, Grid, Divider, Alert, List, ListItem, ListItemIcon,
  ListItemText, Tabs, Tab,
} from "@mui/material";
import ExpandMoreIcon        from "@mui/icons-material/ExpandMore";
import CheckCircleIcon       from "@mui/icons-material/CheckCircle";
import InfoIcon              from "@mui/icons-material/Info";
import WarningIcon           from "@mui/icons-material/Warning";
import AssignmentIcon        from "@mui/icons-material/Assignment";
import PeopleIcon            from "@mui/icons-material/People";
import ReceiptLongIcon       from "@mui/icons-material/ReceiptLong";
import AccessTimeIcon        from "@mui/icons-material/AccessTime";
import NotificationsIcon     from "@mui/icons-material/Notifications";
import SettingsIcon          from "@mui/icons-material/Settings";
import BuildIcon             from "@mui/icons-material/Build";
import LinkIcon              from "@mui/icons-material/Link";
import TimelineIcon          from "@mui/icons-material/Timeline";
import PrintIcon             from "@mui/icons-material/Print";

const cardSx = {
  borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)",
  boxShadow:"0 4px 16px rgba(0,0,0,0.05)", p:3, mb:2,
};

const sectionTitleSx = { fontWeight:800, color:"#223142", mb:1 };
const bodyTextSx     = { color:"#374151", lineHeight:1.8 };

const Badge = ({ label, color = "#2f63ad" }) => (
  <Chip size="small" label={label}
    sx={{ fontWeight:700, fontSize:11, borderRadius:"8px", mr:0.5, mb:0.5,
      bgcolor:`${color}18`, color }} />
);

// ── Section components ──────────────────────────────────────────────
const OverviewSection = () => (
  <Box>
    <Alert severity="info" icon={<AssignmentIcon />} sx={{ mb:2.5, borderRadius:2 }}>
      Work Orders are the core maintenance tracking tool. Every maintenance request - from a leaky faucet to a
      full renovation - flows through this module, from creation to billing.
    </Alert>
    <Grid container spacing={2} sx={{ mb:2 }}>
      {[
        { icon:<AssignmentIcon sx={{ color:"#2f63ad" }} />, title:"Create & Track", desc:"Log any maintenance request with type, subtype, location, priority, and assignee." },
        { icon:<PeopleIcon sx={{ color:"#0f766e" }} />,     title:"Assign & Notify", desc:"Assign to one or multiple staff. Members receive automatic notifications on status changes." },
        { icon:<AccessTimeIcon sx={{ color:"#b45309" }} />, title:"Time Tracking",  desc:"Clock in/out on site or manually log hours. Billable vs. non-billable time tracked separately." },
        { icon:<ReceiptLongIcon sx={{ color:"#7c3aed" }} />,title:"Billing",        desc:"Auto-generate billing records based on configured rules. Labor + materials billing supported." },
      ].map(({ icon, title, desc }) => (
        <Grid item xs={12} sm={6} key={title}>
          <Paper elevation={0} sx={{ borderRadius:"12px", border:"1px solid rgba(47,99,173,0.08)", p:2, height:"100%" }}>
            <Box sx={{ display:"flex", gap:1.5, alignItems:"flex-start" }}>
              <Box sx={{ mt:0.25 }}>{icon}</Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142" }}>{title}</Typography>
                <Typography variant="body2" sx={{ color:"#6b778c", mt:0.25 }}>{desc}</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      ))}
    </Grid>
  </Box>
);

const CreatingWOSection = () => (
  <Box>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:2 }}>
      Work orders can be created from two places: the main Work Orders list page, or directly from a
      member's profile page (pre-fills the property).
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:1 }}>Step-by-Step</Typography>
    {[
      { step:"1", title:"Open the form", desc:'Click the blue "New Work Order" button on the Work Orders page, or "Create Work Order" on a member profile.' },
      { step:"2", title:"Select the type", desc:"Choose the work order type. The subtype and location dropdowns will filter automatically based on the type selected." },
      { step:"3", title:"Fill in details", desc:"Enter a short description and detailed notes. Attach photos or documents using the drag-and-drop file area." },
      { step:"4", title:"Set priority", desc:"Low / Medium / High / Critical. Critical work orders appear highlighted in red in the list view." },
      { step:"5", title:"Notify member", desc:'Check "Notify Member" to send an automatic email when the WO is created. Uncheck it for internal-only tickets.' },
      { step:"6", title:"Submit", desc:"Click Save. The work order is created and assignees are notified by email if configured." },
    ].map(({ step, title, desc }) => (
      <Box key={step} sx={{ display:"flex", gap:2, mb:1.5 }}>
        <Box sx={{ minWidth:28, height:28, borderRadius:"50%", bgcolor:"rgba(47,99,173,0.12)", display:"flex", alignItems:"center", justifyContent:"center" }}>
          <Typography variant="caption" sx={{ fontWeight:800, color:"#2f63ad" }}>{step}</Typography>
        </Box>
        <Box>
          <Typography variant="body2" sx={{ fontWeight:700, color:"#223142" }}>{title}</Typography>
          <Typography variant="body2" sx={{ color:"#6b778c" }}>{desc}</Typography>
        </Box>
      </Box>
    ))}
    <Alert severity="warning" icon={<WarningIcon />} sx={{ mt:2, borderRadius:2 }}>
      If the "Notify Member" checkbox does not appear on the form, an admin has hidden it via the
      Form Builder in System Preferences. Contact your system administrator.
    </Alert>
  </Box>
);

const StatusSection = () => (
  <Box>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:2 }}>
      Statuses track the lifecycle of a work order. They are fully customizable per HOA.
      Common workflow: <Badge label="Open" color="#2f63ad" /> <Badge label="In Progress" color="#b45309" /> <Badge label="Pending Parts" color="#7c3aed" /> <Badge label="Completed" color="#16a34a" /> <Badge label="Closed" color="#374151" />
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:1 }}>Changing Status</Typography>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:2 }}>
      Open the work order detail and use the Status dropdown in the Details tab. Changing status will:
    </Typography>
    <List dense>
      {[
        "Record the change in the Status History tab with a timestamp and who made the change",
        'Send a notification email to the member if "Notify Member" is enabled for this WO',
        "Trigger any billing rules configured to fire on that status transition",
        "Update the SLA countdown if a due date or SLA target is set",
      ].map(t => (
        <ListItem key={t} sx={{ py:0.25 }}>
          <ListItemIcon sx={{ minWidth:28 }}><CheckCircleIcon fontSize="small" sx={{ color:"#16a34a" }} /></ListItemIcon>
          <ListItemText primary={<Typography variant="body2" sx={{ color:"#374151" }}>{t}</Typography>} />
        </ListItem>
      ))}
    </List>
    <Alert severity="info" sx={{ mt:2, borderRadius:2 }}>
      To add or rename statuses, go to System Preferences &rarr; Work Orders &rarr; Statuses tab.
    </Alert>
  </Box>
);

const AssigningSection = () => (
  <Box>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:2 }}>
      Work orders support multiple assignees. The first assignee is the primary responsible person.
    </Typography>
    {[
      { title:"Assign on creation", desc:"Select one or more staff members in the Assigned To field when creating the WO." },
      { title:"Reassign later", desc:"Open the WO, go to the Details tab, and update the Assignees field. Save to apply." },
      { title:"Bulk assign", desc:'From the Work Orders list, select multiple WOs using the checkboxes and use the "Bulk Assign" action.' },
      { title:"Assignment notifications", desc:"Assignees receive an email notification automatically when assigned. Email templates are editable in System Preferences.' "},
    ].map(({ title, desc }) => (
      <Paper key={title} elevation={0} sx={{ borderRadius:"10px", border:"1px solid rgba(47,99,173,0.08)", p:1.5, mb:1 }}>
        <Typography variant="body2" sx={{ fontWeight:700, color:"#223142" }}>{title}</Typography>
        <Typography variant="body2" sx={{ color:"#6b778c", mt:0.25 }}>{desc}</Typography>
      </Paper>
    ))}
  </Box>
);

const TimeLogsSection = () => (
  <Box>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:2 }}>
      The Time Logs tab lets staff record hours worked. There are two ways to track time:
    </Typography>
    <Grid container spacing={2} sx={{ mb:2 }}>
      <Grid item xs={12} sm={6}>
        <Paper elevation={0} sx={{ borderRadius:"12px", border:"2px solid rgba(22,163,74,0.2)", p:2, height:"100%" }}>
          <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#16a34a", mb:1 }}>Clock In / Out (Live)</Typography>
          <Typography variant="body2" sx={{ color:"#374151" }}>
            Go to the Clock In/Out tab. Click "Clock In" when arriving on site and "Clock Out" when leaving.
            The system records the exact start/end time and calculates the duration automatically.
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Paper elevation={0} sx={{ borderRadius:"12px", border:"2px solid rgba(47,99,173,0.2)", p:2, height:"100%" }}>
          <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#2f63ad", mb:1 }}>Manual Entry</Typography>
          <Typography variant="body2" sx={{ color:"#374151" }}>
            Go to the Time Logs tab and click "Add Time Log". Enter start/end times and mark whether
            the time is billable. Add notes for context.
          </Typography>
        </Paper>
      </Grid>
    </Grid>
    <Alert severity="info" sx={{ borderRadius:2 }}>
      The Time Logs Summary box shows total hours, billable hours, and the dollar amount based on your configured labor rate.
    </Alert>
  </Box>
);

const BillingSection = () => (
  <Box>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:2 }}>
      Work Orders can generate billing records that are sent to your billing integration (e.g. Jonas).
      Billing is composed of labor time and/or materials.
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:1 }}>Billing Workflow</Typography>
    {[
      { step:"1", title:"Configure billing rules", desc:"In System Preferences > Work Orders > Billing Config, set up rules: which work order types are billable, what rate to use, and which billing code to apply." },
      { step:"2", title:"Work is completed", desc:"Staff logs their time (Time Logs tab) and any materials used (Materials tab) against the WO." },
      { step:"3", title:"Generate billing", desc:'Click the "Generate Billing" button in the WO header. The system finds the matching rule and creates a billing record.' },
      { step:"4", title:"Manual override", desc:"If no rule matches, a dialog prompts you to enter the labor and materials amounts manually before submitting." },
      { step:"5", title:"Review in Integrations", desc:"The billing record appears in Integrations > Billing. From there, it can be approved and synced to Jonas." },
    ].map(({ step, title, desc }) => (
      <Box key={step} sx={{ display:"flex", gap:2, mb:1.5 }}>
        <Box sx={{ minWidth:28, height:28, borderRadius:"50%", bgcolor:"rgba(124,58,237,0.1)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          <Typography variant="caption" sx={{ fontWeight:800, color:"#7c3aed" }}>{step}</Typography>
        </Box>
        <Box>
          <Typography variant="body2" sx={{ fontWeight:700, color:"#223142" }}>{title}</Typography>
          <Typography variant="body2" sx={{ color:"#6b778c" }}>{desc}</Typography>
        </Box>
      </Box>
    ))}
  </Box>
);

const TemplatesSection = () => (
  <Box>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:2 }}>
      Templates let you pre-fill common work orders so staff can create them with one click.
      Useful for recurring maintenance like HVAC filter changes or pool chemical checks.
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:1 }}>Creating a Template</Typography>
    <Typography variant="body2" sx={{ color:"#6b778c", mb:1.5 }}>
      Go to System Preferences &rarr; Work Orders &rarr; Advanced Settings &rarr; WO Templates.
    </Typography>
    <List dense>
      {[
        "Set the template name (e.g. 'Monthly Pool Inspection')",
        "Optionally pre-select Type, Subtype, and Location",
        "Add default Description and Details text",
        "Set a default priority",
        "Click Save Template",
      ].map(t => (
        <ListItem key={t} sx={{ py:0.25 }}>
          <ListItemIcon sx={{ minWidth:28 }}><CheckCircleIcon fontSize="small" sx={{ color:"#2f63ad" }} /></ListItemIcon>
          <ListItemText primary={<Typography variant="body2" sx={{ color:"#374151" }}>{t}</Typography>} />
        </ListItem>
      ))}
    </List>
  </Box>
);

const LinksSection = () => (
  <Box>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:2 }}>
      Work orders can be linked together to show relationships (e.g. follow-up work, duplicate reports,
      or work that is blocked by another WO).
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:1 }}>Adding a Link</Typography>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:2 }}>
      Open any work order and go to the Links tab. Enter the WO ID you want to link, select the relationship
      type (Related / Blocks / Duplicate), and click Add.
    </Typography>
    <Alert severity="info" sx={{ borderRadius:2 }}>
      Links are bidirectional. If WO #123 blocks WO #456, both work orders show the relationship.
    </Alert>
  </Box>
);

const CommentsSection = () => (
  <Box>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:2 }}>
      The Comments tab is used for all communication and notes on a work order.
    </Typography>
    {[
      { type:"External", color:"#2f63ad", desc:"Visible to the member if they have portal access. Use for updates you want the homeowner to see." },
      { type:"Internal", color:"#b45309", desc:"Only visible to staff. Use for notes, instructions, or details you do not want the member to see." },
    ].map(({ type, color, desc }) => (
      <Paper key={type} elevation={0} sx={{ borderRadius:"10px", border:`1px solid ${color}30`, p:1.5, mb:1 }}>
        <Box sx={{ display:"flex", alignItems:"center", gap:1, mb:0.5 }}>
          <Chip size="small" label={type} sx={{ bgcolor:`${color}18`, color, fontWeight:700, fontSize:11, borderRadius:"6px" }} />
        </Box>
        <Typography variant="body2" sx={{ color:"#374151" }}>{desc}</Typography>
      </Paper>
    ))}
    <Typography variant="body2" sx={{ color:"#6b778c", mt:1 }}>
      Comments can include file attachments (images, PDFs, docs). Each comment shows the author name
      and timestamp, and an edit history is available if the comment is modified later.
    </Typography>
  </Box>
);

const SLASection = () => (
  <Box>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:2 }}>
      SLA (Service Level Agreement) targets define how many days a work order type should take to resolve.
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:1 }}>Setup</Typography>
    <Typography variant="body2" sx={{ color:"#6b778c", mb:2 }}>
      Go to System Preferences &rarr; Work Orders &rarr; Advanced Settings &rarr; SLA Targets.
      Set the target days for each work order type.
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:1 }}>How it Works</Typography>
    {[
      "WOs exceeding 80% of the SLA target show a yellow warning indicator",
      "WOs past 100% of the target show a red overdue indicator",
      "The SLA endpoint (/work-orders/sla-status) can be polled for dashboard reporting",
      "Due dates can also be set manually per WO in the Details tab",
    ].map(t => (
      <Box key={t} sx={{ display:"flex", gap:1, mb:1 }}>
        <CheckCircleIcon fontSize="small" sx={{ color:"#2f63ad", mt:"2px", flexShrink:0 }} />
        <Typography variant="body2" sx={{ color:"#374151" }}>{t}</Typography>
      </Box>
    ))}
  </Box>
);

const AdminSection = () => (
  <Box>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:2 }}>
      All Work Orders settings are in System Preferences &rarr; Work Orders.
    </Typography>
    <Grid container spacing={1.5}>
      {[
        { tab:"Types",            desc:"Create and manage work order types (e.g. Plumbing, Electrical). Each type can have its own SLA target." },
        { tab:"Subtypes",         desc:"Add subtypes per type for more granular categorization (e.g. Plumbing > Leak, Plumbing > Clog)." },
        { tab:"Locations",        desc:"Define locations per type (e.g. Pool Area, Clubhouse, Unit) for where work is performed." },
        { tab:"Billing Config",   desc:"Set up billing rules: which types are billable, what rates to use, and which billing codes to assign." },
        { tab:"Statuses",         desc:"Add, rename, reorder, or deactivate statuses. All existing WOs retain their current status value." },
        { tab:"Form Builder",     desc:"Control which fields appear on the Work Order submission form and rename field labels to match your HOA terminology." },
        { tab:"Advanced Settings",desc:"Manage SLA targets per type, create reusable WO templates, and configure webhook integrations." },
        { tab:"Manual",           desc:"This page. Use it to onboard new staff or provide quick reference to any user." },
      ].map(({ tab, desc }) => (
        <Grid item xs={12} sm={6} key={tab}>
          <Paper elevation={0} sx={{ borderRadius:"10px", border:"1px solid rgba(47,99,173,0.08)", p:1.5 }}>
            <Typography variant="body2" sx={{ fontWeight:700, color:"#2f63ad" }}>{tab}</Typography>
            <Typography variant="body2" sx={{ color:"#6b778c", mt:0.25, fontSize:12 }}>{desc}</Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  </Box>
);

const PermissionsSection = () => (
  <Box>
    <Typography variant="body2" sx={{ ...bodyTextSx, mb:2 }}>
      Access to Work Orders features is controlled by the roles assigned to each user.
    </Typography>
    <Paper elevation={0} sx={{ borderRadius:"12px", border:"1px solid rgba(47,99,173,0.08)", overflow:"hidden" }}>
      <table style={{ width:"100%", borderCollapse:"collapse" }}>
        <thead>
          <tr style={{ background:"rgba(47,99,173,0.04)" }}>
            {["Feature","Required Permission"].map(h => (
              <th key={h} style={{ padding:"8px 12px", textAlign:"left", fontSize:12, fontWeight:700, color:"#223142", borderBottom:"1px solid rgba(47,99,173,0.08)" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[
            ["View Work Orders",       "view_work_orders (or any authenticated user)"],
            ["Create Work Order",      "create_work_orders"],
            ["Edit Work Order",        "edit_work_orders"],
            ["Delete / Soft-Delete",   "delete_work_orders"],
            ["Generate Billing",       "manage_billing_settings"],
            ["Bulk Operations",        "edit_work_orders"],
            ["Export CSV",             "view_work_orders"],
            ["System Preferences",     "Admin or System Admin role"],
          ].map(([feat, perm]) => (
            <tr key={feat} style={{ borderBottom:"1px solid rgba(47,99,173,0.05)" }}>
              <td style={{ padding:"7px 12px", fontSize:12, color:"#223142", fontWeight:600 }}>{feat}</td>
              <td style={{ padding:"7px 12px", fontSize:12, color:"#6b778c", fontFamily:"monospace" }}>{perm}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Paper>
  </Box>
);

// ── FAQ ─────────────────────────────────────────────────────────────
const FAQSection = () => {
  const items = [
    {
      q: "Why isn't the member receiving email notifications?",
      a: "Check two things: (1) The work order has 'Notify Member' enabled (toggle in the Details tab or set as default in the Form Builder). (2) The member's email address is on file and not bounced. Check the Email Delivery Log in System Preferences.",
    },
    {
      q: "How do I change the terminology from 'Work Order' to something else (e.g. 'Green Sheet')?",
      a: "Go to System Preferences > Work Orders > Form Builder. The Form Builder lets you rename field labels. For module-level terminology, contact your system administrator to update email templates.",
    },
    {
      q: "The billing code is not showing on the final billing report.",
      a: "Make sure the billing rule for this work order type has a billing code assigned (System Preferences > Work Orders > Billing Config). The billing code column on the report comes from the rule that matched.",
    },
    {
      q: "Can I recover a deleted work order?",
      a: "Yes. Work orders are soft-deleted (they are never permanently removed). An administrator can restore them via the API endpoint POST /api/work-orders/:id/restore or by contacting your system admin.",
    },
    {
      q: "Why does the Materials tab not appear?",
      a: "The Materials tab is only shown when the 'Materials Billing' feature flag is enabled. Go to System Preferences > Work Orders > Advanced Settings > Features and enable it.",
    },
    {
      q: "How do I run a report on all overdue work orders?",
      a: "Use the Work Orders list page and filter by status. For a full SLA breach report, the AI chat assistant can pull this - type 'show me all overdue work orders' in the chat.",
    },
  ];
  return (
    <Box>
      {items.map(({ q, a }) => (
        <Accordion key={q} elevation={0}
          sx={{ border:"1px solid rgba(47,99,173,0.08)", borderRadius:"10px !important", mb:1, "&:before":{ display:"none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" sx={{ fontWeight:700, color:"#223142" }}>{q}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ color:"#374151" }}>{a}</Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

// ── Main ─────────────────────────────────────────────────────────────
const SECTIONS = [
  { label:"Overview",         icon:<AssignmentIcon fontSize="small" />,    component:<OverviewSection /> },
  { label:"Creating a WO",    icon:<BuildIcon fontSize="small" />,         component:<CreatingWOSection /> },
  { label:"Statuses",         icon:<TimelineIcon fontSize="small" />,      component:<StatusSection /> },
  { label:"Assigning",        icon:<PeopleIcon fontSize="small" />,        component:<AssigningSection /> },
  { label:"Time Logs",        icon:<AccessTimeIcon fontSize="small" />,    component:<TimeLogsSection /> },
  { label:"Billing",          icon:<ReceiptLongIcon fontSize="small" />,   component:<BillingSection /> },
  { label:"Templates",        icon:<PrintIcon fontSize="small" />,         component:<TemplatesSection /> },
  { label:"Links",            icon:<LinkIcon fontSize="small" />,          component:<LinksSection /> },
  { label:"Comments",         icon:<NotificationsIcon fontSize="small" />, component:<CommentsSection /> },
  { label:"SLA",              icon:<AccessTimeIcon fontSize="small" />,    component:<SLASection /> },
  { label:"Admin Settings",   icon:<SettingsIcon fontSize="small" />,      component:<AdminSection /> },
  { label:"Permissions",      icon:<InfoIcon fontSize="small" />,          component:<PermissionsSection /> },
  { label:"FAQ",              icon:<InfoIcon fontSize="small" />,          component:<FAQSection /> },
];

const WorkOrderManual = () => {
  const [selected, setSelected] = useState(0);

  return (
    <Box>
      <Box sx={{ mb:3 }}>
        <Typography variant="h6" sx={{ fontWeight:800, color:"#223142" }}>Work Orders - User Manual</Typography>
        <Typography variant="body2" sx={{ color:"#6b778c" }}>
          Reference guide for staff, administrators, and members using the Work Orders module.
        </Typography>
      </Box>

      <Grid container spacing={2.5}>
        {/* Sidebar nav */}
        <Grid item xs={12} md={3}>
          <Paper elevation={0} sx={{ borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)", overflow:"hidden", position:"sticky", top:16 }}>
            {SECTIONS.map(({ label, icon }, i) => (
              <Box key={label} onClick={() => setSelected(i)}
                sx={{ display:"flex", alignItems:"center", gap:1.5, px:2, py:1.25, cursor:"pointer",
                  bgcolor: selected === i ? "rgba(47,99,173,0.08)" : "transparent",
                  borderLeft: selected === i ? "3px solid #2f63ad" : "3px solid transparent",
                  "&:hover":{ bgcolor:"rgba(47,99,173,0.04)" },
                  transition:"all 0.15s",
                }}>
                <Box sx={{ color: selected === i ? "#2f63ad" : "#6b778c" }}>{icon}</Box>
                <Typography variant="body2" sx={{ fontWeight: selected === i ? 700 : 500,
                  color: selected === i ? "#2f63ad" : "#374151", fontSize:13 }}>
                  {label}
                </Typography>
              </Box>
            ))}
          </Paper>
        </Grid>

        {/* Content */}
        <Grid item xs={12} md={9}>
          <Paper elevation={0} sx={{ ...cardSx, mb:0 }}>
            <Box sx={{ display:"flex", alignItems:"center", gap:1, mb:2.5, pb:2, borderBottom:"1px solid rgba(47,99,173,0.08)" }}>
              <Box sx={{ color:"#2f63ad" }}>{SECTIONS[selected].icon}</Box>
              <Typography variant="h6" sx={{ fontWeight:800, color:"#223142" }}>{SECTIONS[selected].label}</Typography>
            </Box>
            {SECTIONS[selected].component}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default WorkOrderManual;
